import { forwardRef, useCallback } from "react";
import { useSnackbar, SnackbarContent } from "notistack";
import { Card, Typography, Box, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { CircleX, X } from "lucide-react";

const ErrorSnack = forwardRef(({ id, message = "Something went wrong", title = "Error" }, ref) => {
  const { closeSnackbar } = useSnackbar();

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  return (
    <SnackbarContent ref={ref}>
      <Card
        sx={{
          bgcolor: "#fff", // The light grey background from your image
          width: "100%",
          maxWidth: "350px", // Standard width
          display: "flex",
          alignItems: "flex-start", // Aligns items to top
          padding: "12px 16px",
          boxShadow: 3, // Adding the shadow you wanted
          borderRadius: 1, // Slight rounding
        }}
      >
        {/* Left Icon */}
        <Box sx={{ display: "flex", pt: 0.5, mr: 2 }}>
          <CircleX color={"rgb(255, 77, 79)"} /> {/* Red Icon */}
        </Box>

        {/* Text Content */}
        <Box sx={{ flexGrow: 1, mr: 10 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: 16 }}>
            {title}
          </Typography>
          <Tooltip title={message} placement="bottom-start" enterDelay={1000}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                fontSize: 14,
                // Ensure long words don't get cropped: allow breaking and wrapping
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
              }}
            >
              {message}
            </Typography>
          </Tooltip>
        </Box>

        {/* Close Button */}
        <IconButton size="small" onClick={handleDismiss} sx={{ mt: -0.5, mr: -1, color: "text.disabled" }}>
          <X />
        </IconButton>
      </Card>
    </SnackbarContent>
  );
});

export default ErrorSnack;

ErrorSnack.displayName = "ErrorSnack";

// 3. Fix "missing in props validation"
ErrorSnack.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
};
